import { Link } from 'gatsby';
import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { useStringQueryParam } from '../../hooks/useStringQueryParam';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { DemoButton } from '../DemoButton';
import { Headline } from '../Headline';
import { GradientBackground } from '../HeroSection/GradientBackground';
import { mobileHeroLeadText, mobileLeadText } from '../MarketingTypography';
import { LogosSection } from '../LogosSection';
import { Reveal } from '../motion';
import { HeatmapMockup } from '../OptimizeProductMockups';
import {
  NewsletterLinkMockup,
  PaymentStatusMockup,
  TransactionDetailMockup
} from '../UseCaseMockups';

const HeroSection = styled('section')`
  position: relative;
  overflow: hidden;
  padding: 96px 0 96px;

  @media (max-width: 960px) {
    padding: 64px 0 64px;
  }
`;

const HeroCopy = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
`;

const HeroDescription = styled('p')`
  font-size: 19px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 680px;
  margin: 24px auto 0;
  ${mobileHeroLeadText}

  @media (max-width: 600px) {
    margin-top: 16px;
  }
`;

const HeroCta = styled('div')`
  margin: 40px auto 0;
`;

const TabsContainer = styled('div')`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 24px;
  margin: 64px auto 56px;

  @media (max-width: 960px) {
    margin: 48px auto 40px;
  }
`;

const Tab = styled('button')<{ active: boolean }>`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 100px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
  background: ${(p) => (p.active ? '#eef3ff' : '#fff')};
  border: 1px solid ${(p) => (p.active ? '#d4e0ff' : '#e2e8f0')};
  color: ${(p) => (p.active ? COLORS.PRIMARY : '#64748b')};

  &:hover {
    border-color: ${COLORS.PRIMARY};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

const StorySection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 64px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StoryContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StoryVisual = styled('div')`
  min-width: 0;

  @media (max-width: 960px) {
    order: -1;
  }
`;

const Tagline = styled('p')`
  font-size: 17px;
  font-weight: 600;
  line-height: 1.5;
  color: #0e1c2b;
  margin: 12px 0 0;
  ${mobileLeadText}
`;

const StoryBody = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 16px 0 0;
  ${mobileLeadText}
`;

const CheckList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckItem = styled('li')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 15px;
  line-height: 1.5;
  color: #4f4f4f;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${COLORS.PRIMARY};
  }
`;

const HighlightsSection = styled('section')`
  max-width: 1100px;
  margin: 0 auto 96px;
  padding: 0 24px;
`;

const HighlightsGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 32px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const CtaRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 36px;
  flex-wrap: wrap;
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

type UseCase = {
  id: string;
  title: string;
  tagline: string;
  description: string;
  benefits: string[];
  result: string;
  productHighlights: string[];
  mockup: React.ComponentType;
};

const useCases: UseCase[] = [
  {
    id: 'news-media',
    title: 'News & Media Publishers',
    tagline: 'Uncover how readers interact, shop, and convert.',
    description:
      'See how readers interact, shop, and convert across every article and traffic source. Affilimate ties purchase behavior to the pages, merchants, and placements your team already tracks, so audience, editorial, and ad sales can work from the same data.',
    benefits: [
      'Audience development ties purchase behavior to subscribers, revealing who converts and which segments drive profit.',
      'Ad sales packages inventory with verified buyer data and page-level performance history.',
      'Editorial learns which stories, merchants, and placements readers prefer and transact on.'
    ],
    result:
      'Commerce decisions across content, audience, and sales start from purchase-backed performance instead of guesswork.',
    productHighlights: [
      'Purchase data tied to audience segments and campaign performance',
      'Page and merchant analytics that show where profit is won or lost',
      'Unified tracking across affiliate, sponsorship, and commerce campaigns',
      'Automated link management and reconciliation',
      'Ask why revenue moved and get answers backed by your own reports',
      'Yield optimization surfaces swap opportunities scoped to your content, ready to review'
    ],
    mockup: HeatmapMockup
  },
  {
    id: 'newsletter',
    title: 'Newsletter',
    tagline: 'From send to sale: finally measurable.',
    description:
      'Affilimate gives newsletter teams full visibility into how email drives affiliate revenue, at scale. Track clicks and revenue per campaign or segment, connect subscriber data to conversions, and enrich your CRM with granular performance insights. With clean, ESP-compatible links and flexible custom fields, every send becomes a measurable driver of growth.',
    benefits: [],
    result: '',
    productHighlights: [
      'Track clicks and revenue per send, segment, or subscriber.',
      'Connect custom fields for richer insights.',
      'Enrich your CRM with conversion data, product context, and other purchase-based behavior signals.',
      'Create ESP-safe links compatible with popular solutions like Sailthru and Beehiiv.',
      'Query send-level performance from Claude or ChatGPT via the Affilimate MCP.'
    ],
    mockup: NewsletterLinkMockup
  },
  {
    id: 'shopping-loyalty',
    title: 'Shopping & Loyalty Platforms',
    tagline: 'From clicks to commissions: unified and ready-to-use.',
    description:
      'Affilimate gives shopping, loyalty, and cashback platforms a centralized source of truth for affiliate revenue across every network and merchant. With real-time APIs, user-level attribution, and automated link creation, you can power rewards, recommendations, and commerce experiences with reliable, scalable affiliate infrastructure.',
    benefits: [],
    result: '',
    productHighlights: [
      'Unified affiliate dashboard across all networks',
      'Custom dimensions for user-level attribution',
      'Real-time link creation and tracking APIs',
      'Payout reporting and reconciliation',
      'Historical views for audit and accuracy',
      'Currency and status normalization for global programs',
      'No-code data access for your AI tools through the Affilimate MCP'
    ],
    mockup: TransactionDetailMockup
  },
  {
    id: 'finance',
    title: 'Finance teams',
    tagline: 'From transaction to payment: finally a clear picture.',
    description:
      "Affilimate gives finance teams instant clarity from transaction to payment, eliminating manual spreadsheets, inconsistent statuses, and weeks-long month-end closes. With unified data across your affiliate networks, you'll always know what's been earned, paid, and outstanding.",
    benefits: [],
    result: '',
    productHighlights: [
      'Real-time sub-ledger with transaction-level detail',
      "Status timeline: See each transaction's full lifecycle",
      'Bonus filtering: Separate IO-backed bonuses from commissions',
      'Historical views: What was owed at any past point in time',
      'Automated payout reconciliation: Match payouts to transactions in seconds',
      'Ask the Agent about earned, paid, and outstanding in plain language'
    ],
    mockup: PaymentStatusMockup
  }
];

export const UseCasesSection = () => {
  const [activeTabParam, setActiveTab] = useStringQueryParam(
    'tab',
    'news-media'
  );

  const validTabIds = useCases.map((uc) => uc.id);
  const isValidTab = validTabIds.includes(activeTabParam);
  const activeTab = isValidTab ? activeTabParam : 'news-media';

  React.useEffect(() => {
    if (activeTabParam && !isValidTab) {
      setActiveTab('news-media');
    }
  }, [activeTabParam, isValidTab, setActiveTab]);

  const activeUseCase =
    useCases.find((uc) => uc.id === activeTab) || useCases[0];
  const Mockup = activeUseCase.mockup;

  return (
    <>
      <HeroSection>
        <GradientBackground />
        <HeroCopy>
          <Reveal>
            <Headline
              component="h1"
              variant="h1"
              style={{ margin: '0 auto', maxWidth: '800px', fontWeight: 900 }}
            >
              Commerce Growth Solutions for Every Kind of Publisher
            </Headline>
            <HeroDescription>
              Uncover how readers interact, shop, and convert across every
              article and traffic source. Affilimate turns affiliate data into
              revenue intelligence and helps your team act on it.
            </HeroDescription>
            <HeroCta>
              <DemoButton text="Book a demo" variant="contained" />
            </HeroCta>
          </Reveal>
        </HeroCopy>
      </HeroSection>

      <LogosSection />

      <TabsContainer>
        {useCases.map((useCase) => (
          <Tab
            key={useCase.id}
            active={activeTab === useCase.id}
            onClick={() => setActiveTab(useCase.id)}
          >
            {useCase.title}
          </Tab>
        ))}
      </TabsContainer>

      <StorySection key={activeUseCase.id}>
        <StoryContent>
          <Reveal>
            <Headline component="h2" variant="h3" style={{ margin: 0 }}>
              {activeUseCase.title}
            </Headline>
            <Tagline>{activeUseCase.tagline}</Tagline>
            <StoryBody>{activeUseCase.description}</StoryBody>
            {activeUseCase.benefits.length > 0 && (
              <>
                <StoryBody>
                  When revenue data becomes intelligence, every team wins:
                </StoryBody>
                <CheckList>
                  {activeUseCase.benefits.map((benefit, index) => (
                    <CheckItem key={index}>
                      <Check size={16} />
                      {benefit}
                    </CheckItem>
                  ))}
                </CheckList>
                {activeUseCase.result && (
                  <StoryBody>{activeUseCase.result}</StoryBody>
                )}
              </>
            )}
          </Reveal>
        </StoryContent>
        <StoryVisual>
          <Reveal delay={150}>
            <Mockup />
          </Reveal>
        </StoryVisual>
      </StorySection>

      <HighlightsSection key={`highlights-${activeUseCase.id}`}>
        <Headline component="h3" variant="h4" style={{ margin: '0 0 20px' }}>
          Product highlights:
        </Headline>
        <HighlightsGrid>
          {activeUseCase.productHighlights.map((highlight, index) => (
            <CheckItem key={index}>
              <Check size={16} />
              {highlight}
            </CheckItem>
          ))}
        </HighlightsGrid>
        <CtaRow>
          <DemoButton text="Book a demo" variant="contained" />
          <InlineLink to="/product/">
            See all features <ArrowRight size={18} />
          </InlineLink>
        </CtaRow>
      </HighlightsSection>
    </>
  );
};
